 

import React from 'react';
import { Pagination } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getPagedData, setPagination } from '@store/DynamicSlice/dynamicTableSlice';

interface DynamicPaginationProps {
  tableKey: string;
  className?: string;
  maxVisiblePages?: number;
  showItemsPerPage?: boolean;
  showGoToPage?: boolean;
}

interface TableState {
  pageIndex: number;
  pageSize: number;
  totalItems: number;
}

const defaultTableState: TableState = {
  pageIndex: 1,
  pageSize: 10,
  totalItems: 0,
};

export const DynamicPagination: React.FC<DynamicPaginationProps> = ({
  tableKey,
  className,
  maxVisiblePages = 5,
  showItemsPerPage = true,
  showGoToPage = false,
}) => {
  const dispatch = useAppDispatch();
  const tableState = useAppSelector(
    (state) => (state.dynamicTable as any)[tableKey] as TableState | undefined
  );

  const { pageIndex, pageSize, totalItems } = tableState ?? defaultTableState;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // إذا كان هناك صفحة واحدة فقط أو أقل، لا نعرض الباجينيشن
  if (totalPages <= 1) return null;

  // Detect direction (RTL/LTR)
  const dir =
    (typeof document !== 'undefined' &&
      (document.documentElement.getAttribute('dir') || document.body.getAttribute('dir'))) ||
    'ltr';

  const isRTL = String(dir).toLowerCase() === 'rtl';

  const ICON = {
    prev: isRTL ? 'fa-chevron-right' : 'fa-chevron-left',
    next: isRTL ? 'fa-chevron-left' : 'fa-chevron-right',
    first: isRTL ? 'fa-angles-right' : 'fa-angles-left',
    last: isRTL ? 'fa-angles-left' : 'fa-angles-right',
  };

  // حساب العناصر المعروضة حالياً
  const startItem = Math.min((pageIndex - 1) * pageSize + 1, totalItems);
  const endItem = Math.min(pageIndex * pageSize, totalItems);

  const getPageNumbers = () => {
    const pages: number[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const startPage = Math.max(
      1,
      Math.min(pageIndex - Math.floor(maxVisiblePages / 2), totalPages - maxVisiblePages + 1)
    );
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    dispatch(setPagination({ tableKey, pageIndex: newPage, pageSize }));
    dispatch(getPagedData({ tableKey }));
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`w-100 ${className ?? ''}`}>
      <div className='row align-items-center g-3'>
        {/* معلومات العرض */}
        <div className='col-12 col-md-auto order-1 order-md-1'>
          <div className='text-muted small d-flex align-items-center'>
            {/* <i className='fas fa-list-check me-2 d-none d-md-inline'></i> */}
            <span>
              عرض <span className='fw-bold text-theme'>{startItem}</span> إلى{' '}
              <span className='fw-bold text-theme'>{endItem}</span> من{' '}
              <span className='fw-bold'>{totalItems}</span> عنصر
            </span>
          </div>
        </div>

        {/* عناصر الترقيم */}
        <div className='col-12 col-md order-3 order-md-2'>
          <div className='d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-2'>
            {/* Items per page (Desktop) */}
            {showItemsPerPage && (
              <div className='d-none d-md-flex align-items-center'>
                <span className='me-2 small text-muted'>عدد:</span>
                <select
                  className='form-select form-select-sm w-auto'
                  value={pageSize}
                  onChange={(e) => {
                    const newPageSize = Number(e.target.value);
                    dispatch(setPagination({ tableKey, pageIndex: 1, pageSize: newPageSize }));
                    dispatch(getPagedData({ tableKey }));
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            )}

            {/* Pagination */}
            <div dir={isRTL ? 'rtl' : 'ltr'}>
              {/* Mobile */}
              <Pagination className='mb-0 d-flex d-md-none align-items-center flex-wrap'>
                <Pagination.Prev
                  onClick={() => handlePageChange(Math.max(1, pageIndex - 1))}
                  disabled={pageIndex === 1}
                  title='السابقة'
                >
                  <i className={`fas ${ICON.prev}`}></i>
                </Pagination.Prev>

                <Pagination.Item active className='px-2' disabled>
                  <span className='fw-bold'>{pageIndex}</span>
                  <span className='mx-1 text-muted'>/</span>
                  <span>{totalPages}</span>
                </Pagination.Item>

                <Pagination.Next
                  onClick={() => handlePageChange(Math.min(totalPages, pageIndex + 1))}
                  disabled={pageIndex === totalPages}
                  title='التالية'
                >
                  <i className={`fas ${ICON.next}`}></i>
                </Pagination.Next>
              </Pagination>

              {/* Desktop */}
              <Pagination className='mb-0 d-none d-md-flex align-items-center flex-wrap'>
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={pageIndex === 1}
                  title='الصفحة الأولى'
                >
                  <i className={`fas ${ICON.first}`}></i>
                </Pagination.First>

                <Pagination.Prev
                  onClick={() => handlePageChange(Math.max(1, pageIndex - 1))}
                  disabled={pageIndex === 1}
                  title='الصفحة السابقة'
                >
                  <i className={`fas ${ICON.prev}`}></i>
                </Pagination.Prev>

                {/* أول الصفحة + Ellipsis */}
                {pageNumbers[0] > 1 && (
                  <>
                    <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                    {pageNumbers[0] > 2 && <Pagination.Ellipsis disabled />}
                  </>
                )}

                {/* أرقام الصفحات */}
                {pageNumbers.map((p) => (
                  <Pagination.Item
                    key={p}
                    active={p === pageIndex}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </Pagination.Item>
                ))}

                {/* آخر الصفحة + Ellipsis */}
                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                      <Pagination.Ellipsis disabled />
                    )}
                    <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                      {totalPages}
                    </Pagination.Item>
                  </>
                )}

                <Pagination.Next
                  onClick={() => handlePageChange(Math.min(totalPages, pageIndex + 1))}
                  disabled={pageIndex === totalPages}
                  title='الصفحة التالية'
                >
                  <i className={`fas ${ICON.next}`}></i>
                </Pagination.Next>

                <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={pageIndex === totalPages}
                  title='الصفحة الأخيرة'
                >
                  <i className={`fas ${ICON.last}`}></i>
                </Pagination.Last>
              </Pagination>
            </div>

            {/* Go to page */}
            {showGoToPage && (
              <div className='d-none d-lg-flex align-items-center'>
                <span className='me-2 small text-muted'>اذهب:</span>
                <div className='input-group input-group-sm w-auto'>
                  <input
                    type='number'
                    className='form-control text-center'
                    min={1}
                    max={totalPages}
                    value={pageIndex}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
                        handlePageChange(value);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Items per page (Mobile) */}
        {showItemsPerPage && (
          <div className='col-12 order-2 order-md-3 d-flex d-md-none justify-content-center'>
            <div className='d-flex align-items-center'>
              <span className='me-2 small text-muted'>عدد العناصر:</span>
              <select
                className='form-select form-select-sm w-auto'
                value={pageSize}
                onChange={(e) => {
                  const newPageSize = Number(e.target.value);
                  dispatch(setPagination({ tableKey, pageIndex: 1, pageSize: newPageSize }));
                  dispatch(getPagedData({ tableKey }));
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicPagination;
